/**
 * The face a set wears when the lane answered with nothing.
 *
 * No fixture exercises this and it ships anyway, because "no results" is a
 * real answer that a research surface returns every day. It has to be
 * unmistakably different from BOTH of its neighbours: a reader must never have
 * to work out whether a surface is still loading, came back empty, or failed.
 * This one is the quiet, finished one — the shared empty-state artwork, a
 * stated reason, and no motion beyond the artwork's own arrival.
 *
 * The reason is required for the same purpose it is required on
 * A missing row value and an empty result set answer different questions:
 * are different answers, and only the caller knows which one it got.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import { Cemptystate } from "@/components/ui/Cemptystate";
import { CRC_TEXT_MUTED, CRC_TYPE_STYLE } from "./cresultcard.tokens";
import { CresultcardTextStep } from "./cresultcard.types";

/**
 * The shared empty state floors at 350px, which is taller than most result
 * widgets are. Inside a card it is scaled to the widget's own body.
 */
const FACE_MIN_HEIGHT = 176;
const EMPTY_TEXT_SIZE = 22;

interface CresultcardEmptyFaceProps {
  /** The line on the artwork — what was looked for and not found. */
  readonly headline: string;
  /** Why the set is empty, stated rather than implied. */
  readonly reason: string;
  /** What a reader can do about it, when there is something. */
  readonly action?: ReactNode;
}

export function CresultcardEmptyFace({
  headline,
  reason,
  action,
}: CresultcardEmptyFaceProps): ReactElement {
  return (
    <div
      data-el="Cresultcard/EmptyFace/Status"
      data-crc-face="empty"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        minWidth: 0,
        paddingBlock: 18,
        paddingInline: 14,
      }}
    >
      <Cemptystate
        dataElScope="Cresultcard/CresultcardEmptyFace"
        style={{ minHeight: FACE_MIN_HEIGHT }}
        text={headline}
        textSize={EMPTY_TEXT_SIZE}
      />
      <span
        style={{
          ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
          maxWidth: "44ch",
          textAlign: "center",
          color: CRC_TEXT_MUTED,
        }}
      >
        {reason}
      </span>
      {action}
    </div>
  );
}
